import React, { useEffect, useMemo, useState } from "react";
import EventReviewApi from "../api/EventReviewApi";
import ReviewGuestList from "./ReviewGuestList";
import {
  NOT_ASKED_TEXT,
  averageText,
  distributionRows,
  isPermissionDenied,
  notEnoughReviewsText,
  reviewCount,
  reviewGuestRows,
  reviewSummaryState,
  summaryComments,
} from "./utils/reviews";

// Shown when the summary doc is missing but an admin can see guests were asked.
// The send writes a count-0 summary once it has asked everyone, and the first
// answer writes one too, so this only covers the moments while the send is
// still asking, or a summary write that failed before anyone answered. Either
// way "not asked" would be wrong. Hosts cannot read the per-guest docs, so
// they keep the contract's line.
const NO_ANSWERS_YET_TEXT = "Guests were asked, and nobody has answered yet.";

const Muted = ({ children }) => (
  <p className="text-sm text-gray-500 dark:text-gray-400">{children}</p>
);

const SummaryCard = ({ summary }) => {
  const average = averageText(summary);
  const count = reviewCount(summary);
  const rows = distributionRows(summary);
  const comments = summaryComments(summary);
  return (
    <div className="space-y-4">
      <div className="flex items-end gap-3">
        <p className="text-3xl font-bold text-gray-900 dark:text-gray-100">{average ?? "-"}</p>
        <p className="pb-1 text-sm text-gray-500 dark:text-gray-400">
          out of 5, from {count} {count === 1 ? "rating" : "ratings"}
        </p>
      </div>
      <ul className="space-y-1" aria-label="How guests rated it">
        {rows.map((row) => (
          <li key={row.rating} className="flex items-center gap-2 text-sm">
            <span className="w-3 text-right text-gray-600 dark:text-gray-300">{row.rating}</span>
            <span className="relative h-2 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
              <span
                className="absolute inset-y-0 left-0 rounded-full bg-amber-400"
                style={{ width: `${Math.round(row.share * 100)}%` }}
              />
            </span>
            <span className="w-6 text-right text-gray-600 dark:text-gray-300">{row.count}</span>
          </li>
        ))}
      </ul>
      <div>
        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">What guests said</p>
        {comments.length === 0 ? (
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">No comments yet.</p>
        ) : (
          <ul className="mt-2 space-y-2">
            {comments.map((comment) => (
              <li
                key={comment.key}
                className="rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-800 whitespace-pre-wrap dark:bg-gray-900 dark:text-gray-200"
              >
                {comment.text}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

/**
 * "How was it?" on a finished event: the day after, guests rate it from 1 to 5
 * and may say what could have been better. The host, co-hosts and admins see
 * the summary (eventReviewSummaries/{eventId}), which never names anyone and
 * holds only a count until 3 or more guests have rated. Admin operators also
 * get each guest's answer below it, read from eventReviews.
 *
 * The rules decide who reads what; EventForm only mounts this for people who
 * can see the event. A refused read is not an error here (a host who opens
 * another host's event gets permission-denied), so it is not logged.
 */
export const Reviews = ({ eventID, eventData, isAdminOperator = false }) => {
  // undefined while loading, null when there is no summary doc.
  const [summary, setSummary] = useState(undefined);
  const [summaryError, setSummaryError] = useState(null); // null | "denied" | "failed"
  useEffect(() => {
    if (!eventID) return undefined;
    setSummary(undefined);
    setSummaryError(null);
    return EventReviewApi.subscribeSummary(
      eventID,
      (data) => {
        setSummary(data);
        setSummaryError(null);
      },
      (err) => {
        if (isPermissionDenied(err)) {
          setSummaryError("denied");
          return;
        }
        console.error("[Reviews] eventReviewSummaries subscription error:", err);
        setSummaryError("failed");
      }
    );
  }, [eventID]);

  // Each guest's review, for admin operators only: undefined while loading,
  // null when not an admin or the read is refused, which just leaves the list out.
  const [reviewsByUser, setReviewsByUser] = useState(undefined);
  useEffect(() => {
    if (!eventID || !isAdminOperator) {
      setReviewsByUser(null);
      return undefined;
    }
    setReviewsByUser(undefined);
    return EventReviewApi.subscribeByEvent(eventID, setReviewsByUser, (err) => {
      if (!isPermissionDenied(err)) console.error("[Reviews] eventReviews subscription error:", err);
      setReviewsByUser(null);
    });
  }, [eventID, isAdminOperator]);

  const guestRows = useMemo(
    () => (reviewsByUser ? reviewGuestRows(reviewsByUser, eventData?.eventUsers) : null),
    [reviewsByUser, eventData?.eventUsers]
  );

  let summaryView;
  if (summaryError === "denied") {
    summaryView = <Muted>You can't see reviews for this event.</Muted>;
  } else if (summaryError === "failed") {
    summaryView = <Muted>Could not load reviews. Reload the page to try again.</Muted>;
  } else if (summary === undefined || (summary === null && reviewsByUser === undefined)) {
    // With no summary doc an admin's line depends on the per-guest list too.
    summaryView = <Muted>Loading reviews...</Muted>;
  } else {
    const state = reviewSummaryState(summary);
    if (state === "visible") {
      summaryView = <SummaryCard summary={summary} />;
    } else if (state === "hidden") {
      summaryView = (
        <div>
          <p className="text-sm text-gray-900 dark:text-gray-100">{notEnoughReviewsText(summary)}</p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The average and comments show once 3 or more guests have rated. Names are never shown.
          </p>
        </div>
      );
    } else {
      summaryView = <Muted>{guestRows && guestRows.length > 0 ? NO_ANSWERS_YET_TEXT : NOT_ASKED_TEXT}</Muted>;
    }
  }

  return (
    <div className="space-y-4">
      {summaryView}
      {isAdminOperator && guestRows && guestRows.length > 0 && (
        <ReviewGuestList eventID={eventID} rows={guestRows} />
      )}
    </div>
  );
};
